import React from 'react';
import {Link} from 'react-router-dom';

import {Avatar} from 'src/components/avatar/avatar';
import {CriteriaStrip, Stripe} from 'src/components/criteria-strip/criteria-strip';
import {ColumnHeader} from 'src/components/filter-header/column-headers/column-header';
import {DateColumnHeader} from 'src/components/filter-header/column-headers/date-column-header';
import {DateRange} from 'src/components/filter-header/typedefs';
import {NoResultsRow} from 'src/components/no-results-row/no-results-row';
import {BUTTON_TYPE, RemoveChangeRequest} from 'src/components/remove-change-request/remove-change-request';
import {UserName} from 'src/components/user-name/user-name';
import {SortConfig} from 'src/hooks/useSortingRows';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {COLUMN_CAPTIONS, ColumnTypes, ColumnWidth, SortingColumnTypes} from 'src/modules/change-requests/constants';
import {createPredicates} from 'src/modules/change-requests/filter-utils/createPredicates';
import {extractColumnHeaderData} from 'src/modules/change-requests/filter-utils/extractColumnHeaderData';
import {Filters} from 'src/modules/change-requests/typedefs';
import {AuthorColumnHeader} from 'src/modules/change-requests/views/column-headers/author-column-header';
import {MAPPED_CHANGE_REQUEST_STATUS} from 'src/modules/constants';
import {getUsersInfo} from 'src/modules/users-info/services';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {Role} from 'src/openapi/models/Role';
import {useAppSelector} from 'src/store';

import Group, {Item} from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {formatDate} from '@pb/kit-formatters';

import {changeRequestsClickable} from '../change-requests.module.styl';

type Props = {
    rows: ChangeRequest[],
    preparedRows: ChangeRequest[],
    setFilter: (filterType: string, filterValue: Array<string> | DateRange) => void,
    filters: Filters,
    onSort: (config: SortConfig<SortingColumnTypes>) => void,
    sortConfig: SortConfig<SortingColumnTypes>,
    criteriaStripeData: Stripe[],
    clearAllFilters: () => void,
    intersectRef: (node: Element | null) => void,
    isVisible: boolean,
    hasMore: boolean,
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    onClickChangeRequest: (changeRequest: ChangeRequest, link: boolean) => string | void,
    onlyMyFilter: boolean,
};

const AMOUNT_LEFT_TO_UPLOAD_NEXT = 5;

export const ChangeRequestsView = (props: Props) => {
    const {
        rows,
        preparedRows,
        setFilter,
        filters,
        onSort,
        sortConfig,
        criteriaStripeData,
        clearAllFilters,
        intersectRef,
        isVisible,
        hasMore,
        onClickChangeRequest,
        onlyMyFilter,
    } = props;

    const users = useAppSelector(getUsersInfo);
    const userRoles = useUserRoles();
    const userEmail = useUserEmail();

    return (
        <Group direction="v" gap="xxs">
            <CriteriaStrip data={criteriaStripeData} clearAllFilters={clearAllFilters}/>
            <Table>
                <Head>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_TYPE]}>
                        <ColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_TYPE}
                            sortingColumnType={SortingColumnTypes.EVENT_TYPE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_TYPE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_SOURCE]}>
                        <ColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_SOURCE}
                            sortingColumnType={SortingColumnTypes.EVENT_SOURCE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_SOURCE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_CODE]}>
                        <ColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_CODE}
                            sortingColumnType={SortingColumnTypes.EVENT_CODE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_CODE]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_DESCRIPTION]}>
                        <ColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.EVENT_DESCRIPTION}
                            sortingColumnType={SortingColumnTypes.EVENT_DESCRIPTION}
                            caption={COLUMN_CAPTIONS[ColumnTypes.EVENT_DESCRIPTION]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.AUTHOR]}>
                        <AuthorColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.AUTHOR}
                            sortingColumnType={SortingColumnTypes.AUTHOR}
                            caption={COLUMN_CAPTIONS[ColumnTypes.AUTHOR]}
                            columnDataHandler={extractColumnHeaderData}
                            onlyMyFilter={onlyMyFilter}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.STATUS]}>
                        <ColumnHeader
                            rows={rows}
                            filters={filters}
                            setFilter={setFilter}
                            filterPredicates={createPredicates}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.STATUS}
                            sortingColumnType={SortingColumnTypes.STATUS}
                            caption={COLUMN_CAPTIONS[ColumnTypes.STATUS]}
                            columnDataHandler={extractColumnHeaderData}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.LAST_UPDATED]}>
                        <DateColumnHeader
                            setFilter={setFilter}
                            filters={filters}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.LAST_UPDATED}
                            sortingColumnType={SortingColumnTypes.LAST_UPDATED}
                            caption={COLUMN_CAPTIONS[ColumnTypes.LAST_UPDATED]}
                        />
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.DUE_DATE]}>
                        <DateColumnHeader
                            setFilter={setFilter}
                            filters={filters}
                            onSort={onSort}
                            sortConfig={sortConfig}
                            filteringColumnType={ColumnTypes.DUE_DATE}
                            sortingColumnType={SortingColumnTypes.DUE_DATE}
                            caption={COLUMN_CAPTIONS[ColumnTypes.DUE_DATE]}
                            allowFutureDate={true}
                        />
                    </Cell>
                    {userRoles.includes(Role.ROLE_UDEDT_REVIEWER) && <Cell width="2rem"/>}
                    <Cell width="1rem"/>
                </Head>
                <Body>
                    {
                        preparedRows.length ? (
                            preparedRows.map((row, i, arr) => (
                                <Row
                                    key={i}
                                    className={changeRequestsClickable}
                                    ref={node => (i === arr.length - AMOUNT_LEFT_TO_UPLOAD_NEXT
                                        ? intersectRef(node as Element) : undefined)}>
                                    <Cell onClick={() => onClickChangeRequest(row, false)} title={true}>
                                        {row.event[ColumnTypes.EVENT_TYPE]}
                                    </Cell>
                                    <Cell onClick={() => onClickChangeRequest(row, false)} title={true}>
                                        {row.event[ColumnTypes.EVENT_SOURCE]}
                                    </Cell>
                                    <Cell title={true}>
                                        <Link to={onClickChangeRequest(row, true) as string}>
                                            {row.event[ColumnTypes.EVENT_CODE]}
                                        </Link>
                                    </Cell>
                                    <Cell onClick={() => onClickChangeRequest(row, false)} title={true}>
                                        {row.event[ColumnTypes.EVENT_DESCRIPTION]}
                                    </Cell>
                                    <Cell onClick={() => onClickChangeRequest(row, false)} title={true}>
                                        <Group alignItems="center">
                                            <Avatar user={users[row[ColumnTypes.AUTHOR]]}/>
                                            <UserName email={row[ColumnTypes.AUTHOR]} showActive={true}/>
                                        </Group>
                                    </Cell>
                                    <Cell onClick={() => onClickChangeRequest(row, false)}>
                                        {MAPPED_CHANGE_REQUEST_STATUS[row[ColumnTypes.STATUS]]}
                                    </Cell>
                                    <Cell title={true}>{
                                        `${formatDate(
                                            new Date(row[ColumnTypes.LAST_UPDATED] ?? ''),
                                            'DD-MMM-YYYY',
                                        )}`
                                    }
                                    </Cell>
                                    <Cell title={true}>
                                        {formatDate(row.dueDate)}
                                    </Cell>
                                    {userRoles.includes(Role.ROLE_UDEDT_REVIEWER) && (
                                        <Cell verticalAlign="middle">
                                            <Item style={{fontStyle: 'italic'}}>
                                                {row.event.id ? 'U' : 'N'}
                                            </Item>
                                        </Cell>
                                    )}
                                    <Cell
                                        className="text-align_center"
                                        verticalAlign="middle">
                                        {(row.authorEmail === userEmail || userRoles.includes(Role.ROLE_UDEDT_ADMIN)) &&
                                            row.status !== ChangeRequestStatus.APPROVED && (
                                            <PushNotification anchorOrigin={{
                                                horizontal: HorizontalPositionElement.RIGHT,
                                                vertical: VerticalPositionElement.BOTTOM,
                                            }}>
                                                <RemoveChangeRequest
                                                    changeRequest={row}
                                                    buttonType={BUTTON_TYPE.ICON}
                                                />
                                            </PushNotification>
                                        )}
                                    </Cell>
                                </Row>
                            ))
                        ) : <NoResultsRow colSpan={7}/>
                    }
                </Body>
            </Table>
            {(isVisible && hasMore) ? <Loading fast={true}/> : null}
        </Group>
    );
};
